import _ from 'lodash';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import dynamic from 'next/dynamic';
import { SHOW_COMMEMORATE } from 'src/constants';
import useResponsive from 'src/utils/devices';
import styled from 'styled-components';
import { IMAGE_SIZE } from '../image';

const WidgetHeading = dynamic(import('src/2.0/widgets/widget-heading'), { ssr: true });
const CardByLine = dynamic(import('src/2.0/cards/card-byline'), { ssr: true });
const CardTitle = dynamic(import('src/2.0/cards/card-title'), { ssr: true });

export default function SectionHomepageOne({ id, data, lastestNews, eventDataLayer, sectionLive }) {
  const responsive = useResponsive();
  const isSectionLive = !_.isEmpty(sectionLive);
  const mapSectionLive = map(
    sectionLive.sort((a, b) => parseFloat(b.key) - parseFloat(a.key)),
    item => ({
      title: item.value,
      timeline: item.key
    })
  );

  return (
    <SectionWrapper id={id} className='container' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : '' }}>
      <div className='block-1'>
        <CardByLine
          showH2={true}
          data={data?.newHighlight[0]}
          showBlurb={false}
          showBadge={true}
          imageLoading='eager'
          imageFetchPriority='high'
          imageWidth={1280}
          imageHeight={720}
          eventDataLayer={{
            ...eventDataLayer,
            data: {
              heading: 'HIGHLIGHT',
              title: data?.newHighlight[0]?.title,
              block: '1',
              index: '1'
            }
          }}
          size={IMAGE_SIZE.LG_WEBP}
          showPublishDate={true}
        />
      </div>
      <div className='block-2 flex-between'>
        <CardByLine
          data={data?.newHighlight[1]}
          imageWidth={1280}
          imageHeight={720}
          showBlurb={false}
          showByline={false}
          eventDataLayer={{
            ...eventDataLayer,
            data: {
              heading: 'HIGHLIGHT',
              title: data?.newHighlight[1]?.title,
              block: '2',
              index: '1'
            }
          }}
          showBadge={responsive.isMobile}
          showPublishDate={true}
        />
        <CardByLine
          data={data?.newHighlight[2]}
          imageWidth={1280}
          imageHeight={720}
          showBlurb={false}
          showByline={false}
          eventDataLayer={{
            ...eventDataLayer,
            data: {
              heading: 'HIGHLIGHT',
              title: data?.newHighlight[1]?.title,
              block: '2',
              index: '2'
            }
          }}
          showBadge={responsive.isMobile}
          showPublishDate={true}
        />
      </div>
      {!isEmpty(lastestNews) && (
        <div className='block-3 flex-column'>
          <WidgetHeading heading='Latest News' link='/category/latest-news' isSectionLive={isSectionLive} />
          {isSectionLive ? (
            <div className='block-3-scroll'>
              {map(isSectionLive ? mapSectionLive : lastestNews, (item, index) => (
                <CardTitle
                  key={index}
                  data={item}
                  eventDataLayer={{
                    ...eventDataLayer,
                    data: {
                      heading: 'Latest News',
                      title: item?.title,
                      block: '3',
                      index: index + 1
                    }
                  }}
                  showPublishDate={true}
                  isSectionLive={isSectionLive}
                  sectionLive={sectionLive}
                />
              ))}
            </div>
          ) : (
            <>
              {map(lastestNews?.slice(0, 4), (item, index) => (
                <CardTitle
                  key={index}
                  data={item}
                  eventDataLayer={{
                    ...eventDataLayer,
                    data: {
                      heading: 'Latest News',
                      title: item?.title,
                      block: '3',
                      index: index + 1
                    }
                  }}
                  showPublishDate={true}
                />
              ))}
            </>
          )}

          {!isSectionLive && (
            <div className='more'>
              <a href='/category/latest-news'>อ่านข่าวทั้งหมด</a>
            </div>
          )}
        </div>
      )}
    </SectionWrapper>
  );
}

const SectionWrapper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 2.5fr 1fr 1fr;
  grid-template-rows: 1fr;
  grid-template-areas: 'block-1 block-2 block-3';
  grid-gap: 20px;
  padding: 15px;
  height: 599px;
  /* Let the img's width/height attributes (16:9) reserve space pre-load (avoids CLS).
     card-byline sets height:100% which makes the CSS aspect-ratio ambiguous; auto makes it authoritative. */
  .card-image img {
    height: auto;
  }
  .block-1 {
    grid-area: block-1;
    min-height: 0;
    overflow: hidden;
    .card-image {
      border-radius: 8px;
    }
    .card-title {
      font-size: 26pt;
      line-height: 1;
      letter-spacing: 0;
    }
    .card-blurb {
      -webkit-line-clamp: 2 !important;
    }
  }
  .block-2 {
    grid-area: block-2;
    flex-direction: column;
    gap: var(--gap);
    min-height: 0;
    overflow: hidden;
    .card-image {
      border-radius: 8px;
    }
    .card-title {
      font-size: 20pt;
      font-family: 'DBHelvethaicaXBd', sans-serif;
      font-weight: 500;
      line-height: 1;
      letter-spacing: 0;
      -webkit-line-clamp: 3 !important;
    }
  }
  .block-3 {
    grid-area: block-3;
    gap: var(--gap);
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .block-3-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: var(--gap);

      /* Scrollbar styling */
      scrollbar-width: thin;
      scrollbar-color: #c1c1c1 transparent;
      &::-webkit-scrollbar {
        width: 4px;
      }
      &::-webkit-scrollbar-thumb {
        background-color: #c1c1c1;
        border-radius: 4px;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      .card-title {
        a {
          cursor: default;
          pointer-events: none;
        }
      }
    }

    .text-ellipsis-2 {
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      -webkit-line-clamp: 2;
    }
    .more {
      display: flex;
      justify-content: flex-end;
      cursor: pointer;
      font-size: 20px;
      flex-shrink: 0;
      a {
        color: #0600e7;
        &:hover {
          color: #80abc1;
        }
      }
    }
  }
  @media (max-width: 1024px) {
    grid-template-columns: 1.5fr 1fr;
    grid-template-rows: auto 1fr;
    grid-template-areas: 'block-1 block-3' 'block-2 block-3';
    height: auto;
    min-height: 545px;
    .block-2 {
      flex-direction: row;
      align-items: flex-start;
    }
    .block-3 .block-3-scroll {
      max-height: 480px;
    }
  }
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    grid-template-areas: 'block-1' 'block-2' 'block-3';
    grid-template-rows: auto;
    grid-gap: 0px;
    padding: 15px 10px;
    height: auto;
    min-height: unset;

    .block-1 {
      overflow: visible;
      .card-byline {
        margin-bottom: 15px;
      }
      .card-byline a {
        display: grid;
        grid-template-columns: 1fr;
        grid-template-areas: 'card-title' 'card-image' 'footer';
      }
      .card-image {
        grid-area: card-image;
        border-radius: 8px;
      }
      .card-title {
        grid-area: card-title;
        font-size: 30pt;
        display: block;
        text-align: center;
        line-height: 1.2;
        font-weight: 400;
        letter-spacing: 0;
      }
      .footer {
        grid-area: footer;
        display: block;
        text-align: center;
      }
    }

    .block-2 {
      display: block;
      padding-bottom: 15px;
      overflow: visible;
      .card-byline {
        margin-bottom: 15px;
      }
      .card-title {
        font-size: 20pt;
      }
    }
    .block-1,
    .block-2 {
      .card-byline {
        border-bottom: 1px solid var(--border-blue);
        padding-bottom: 12px;
      }
    }
    .block-3 {
      border: 1px solid var(--border-blue);
      border-top: 0;
      border-left: 0;
      border-right: 0;

      .block-3-scroll {
        max-height: 310px;
        overflow-y: auto;
      }

      .card-title {
        background-color: transparent;
        font-size: 19pt;
        padding: 0 0 0 18px;
        &:before {
          top: 10px;
        }
      }
    }
  }
`;
